import { createFileRoute, Link, Outlet, redirect, useMatches } from '@tanstack/react-router';
import { useGetTenantBySlugQuery } from '@/api/tenants/getTenantBySlug';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Title } from '@/components/composite/Title';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/tenant/$tenantCode')({
  beforeLoad: ({ params, location, context }) => {
    const basePath = `/dashboard/tenant/${params.tenantCode}`;
    if (location.pathname === basePath) {
      throw redirect({
        to: '/dashboard/tenant/$tenantCode/branding',
        params,
      });
    }
    const { allowedTenantIds } = context.adminUser;
    if (allowedTenantIds !== null) {
      const tenant = context.tenants.find((t: any) => (t.code ?? t.slug) === params.tenantCode);
      if (!tenant || !allowedTenantIds.includes(tenant.tenant_id)) {
        throw redirect({ to: '/dashboard' });
      }
    }
  },
  component: TenantSettingsLayoutRoute,
});

function TenantSettingsLayoutRoute() {
  const { tenantCode } = Route.useParams();
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.pathname ?? '';

  const { data: tenant, isLoading } = useGetTenantBySlugQuery(tenantCode);

  const tenantName = tenant?.title || tenant?.nameEn || tenantCode;
  const ipWhitelistEnabled =
    (tenant?.featureConfig as Record<string, Record<string, unknown>> | undefined)?.pickem
      ?.enableIpWhitelist === true;
  const activeTab = currentPath.includes('/compliance')
    ? 'compliance'
    : currentPath.includes('/marketing-consent')
      ? 'marketing-consent'
      : currentPath.includes('/payout-templates')
        ? 'payout-templates'
        : currentPath.includes('/google-ads')
          ? 'google-ads'
          : currentPath.includes('/image-library')
            ? 'image-library'
            : currentPath.includes('/security')
              ? 'security'
              : currentPath.includes('/ip-whitelist')
                ? 'ip-whitelist'
                : currentPath.includes('/features')
                  ? 'features'
                  : 'branding';

  if (isLoading) {
    return (
      <PageContainer>
        <Card className="flex flex-col gap-4 w-full max-w-6xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <Title>Loading...</Title>
              <BackButton />
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-muted-foreground">Loading tenant data...</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!tenant) {
    return (
      <PageContainer>
        <Card className="flex flex-col gap-4 w-full max-w-6xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <Title>Tenant Not Found</Title>
              <BackButton />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-red-500">
              Tenant with code "{tenantCode}" not found
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{
          tenant: 'Tenants',
          [tenantCode]: tenantName,
          branding: 'General',
          'google-ads': 'Google Ads',
          security: 'Security',
          features: 'Features',
          'ip-whitelist': 'IP Whitelist',
          'payout-templates': 'Payout Templates',
          'marketing-consent': 'Marketing Consent',
          compliance: 'Compliance',
          'image-library': 'Image Library',
          placements: 'Placements',
          'gam-config': 'GAM Config',
        }}
        pathOverrides={{
          tenant: '/dashboard/tenants',
        }}
      />

      <Card className="flex flex-col gap-4 w-full max-w-6xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>{tenantName}</Title>
            <BackButton />
          </div>

          <div className="mt-2 border-b">
            <nav className="flex gap-6">
              <Link
                to="/dashboard/tenant/$tenantCode/branding"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'branding' && 'text-foreground border-primary'
                )}
              >
                General
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/google-ads"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'google-ads' && 'text-foreground border-primary'
                )}
              >
                Google Ads
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/security"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'security' && 'text-foreground border-primary'
                )}
              >
                Security
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/features"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'features' && 'text-foreground border-primary'
                )}
              >
                Features
              </Link>

              {ipWhitelistEnabled && (
                <Link
                  to="/dashboard/tenant/$tenantCode/ip-whitelist"
                  params={{ tenantCode }}
                  className={cn(
                    'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                    activeTab === 'ip-whitelist' && 'text-foreground border-primary'
                  )}
                >
                  IP Whitelist
                </Link>
              )}

              <Link
                to="/dashboard/tenant/$tenantCode/payout-templates"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'payout-templates' && 'text-foreground border-primary'
                )}
              >
                Payout Templates
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/marketing-consent"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'marketing-consent' && 'text-foreground border-primary'
                )}
              >
                Marketing Consent
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/compliance"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'compliance' && 'text-foreground border-primary'
                )}
              >
                Compliance
              </Link>

              <Link
                to="/dashboard/tenant/$tenantCode/image-library"
                params={{ tenantCode }}
                className={cn(
                  'pb-3 text-sm font-medium text-muted-foreground border-b-2 border-transparent',
                  activeTab === 'image-library' && 'text-foreground border-primary'
                )}
              >
                Image Library
              </Link>
            </nav>
          </div>
        </CardHeader>

        <CardContent>
          <Outlet />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
